import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./SimilarListings.scss";
import { thumbImgProps } from "../../utils/imageUrl";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const posterThemes = [
  "blue",
  "violet",
  "graphite",
  "green",
  "bronze",
  "wine",
  "ocean",
  "sand",
];

const sanitize = (s) => (s ? String(s).trim() : "");

const getStableTheme = (item, title) => {
  const source = String(item?.listingId || item?.id || title || "similar");
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return posterThemes[Math.abs(hash) % posterThemes.length];
};

const getTitleSizeClass = (title) => {
  const len = String(title || "").trim().length;

  if (len <= 18) return "is-short";
  if (len <= 42) return "is-medium";
  return "is-long";
};

const isWantedListing = (item) => {
  const raw =
    item?.listingType ||
    item?.typeRaw ||
    item?.type ||
    item?.dealType ||
    item?.transferType ||
    "";

  return String(raw).trim().toLowerCase() === "wanted";
};

const normalizeCategory = (category) => {
  const raw = sanitize(category)
    .replace(/^[^\wА-Яа-яІіЇїЄє]+/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return raw;
};

const baseCategory = (category) => {
  const c = normalizeCategory(category);
  if (!c) return "";
  const parts = c
    .split(/>|\/|\|/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts[0] || c;
};

const tokenize = (text) => {
  const t = sanitize(text).toLowerCase();
  if (!t) return [];
  return t
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
};

const BRAND_ALIASES = {
  apple: ["apple", "iphone", "ipad", "macbook", "imac", "mac"],
  samsung: ["samsung", "galaxy", "note", "s23", "s24"],
  xiaomi: ["xiaomi", "mi", "redmi", "poco"],
  google: ["google", "pixel"],
  huawei: ["huawei", "honor"],
};

const detectBrands = (tokens) => {
  const set = new Set(tokens);
  const brands = new Set();
  Object.entries(BRAND_ALIASES).forEach(([brand, aliases]) => {
    if (aliases.some((a) => set.has(a))) brands.add(brand);
  });
  return brands;
};

const scoreSimilarity = (current, candidate) => {
  let score = 0;

  const curCat = normalizeCategory(current.category);
  const candCat = normalizeCategory(candidate.category);
  const curBase = baseCategory(current.category);
  const candBase = baseCategory(candidate.category);

  if (curCat && candCat && curCat === candCat) score += 6;
  else if (curBase && candBase && curBase === candBase) score += 3;

  const curTokens = tokenize(current.title);
  const candTokens = tokenize(candidate.title);

  const curBrands = detectBrands(curTokens);
  const candBrands = detectBrands(candTokens);
  score += [...curBrands].filter((b) => candBrands.has(b)).length * 3;

  const STOP = new Set(["і", "та", "або", "на", "в", "у", "з", "до", "за", "про", "the", "a", "an"]);
  const curSet = new Set(curTokens.filter((t) => !STOP.has(t) && t.length > 2));
  const candSet = new Set(candTokens.filter((t) => !STOP.has(t) && t.length > 2));

  let overlap = 0;
  for (const t of curSet) if (candSet.has(t)) overlap++;
  score += Math.min(overlap, 4);

  return score;
};

const SimilarCard = ({ item }) => {
  const lid = item.listingId || item.id;
  const title = item.title || "Без назви";
  const thumb =
    (Array.isArray(item.photos) && item.photos[0]) ||
    item.mainPhoto ||
    item.photo ||
    null;

  const priceNum = Number(item.price || 0);
  const wanted = isWantedListing(item);
  const theme = getStableTheme(item, title);
  const titleSizeClass = getTitleSizeClass(title);

  return (
    <Link
      to={`/listing/${lid}`}
      className="similar-card"
      aria-label={title || "Оголошення"}
    >
      <div className="similar-card__media">
        {thumb ? (
          <img {...thumbImgProps(thumb, "(max-width: 600px) 45vw, 220px")} alt={title || "Фото"} />
        ) : wanted ? (
          <div className={`similar-card__poster similar-card__poster--${theme}`}>
            <div className="similar-card__poster-glow" />
            <div className={`similar-card__poster-title ${titleSizeClass}`}>
              {title}
            </div>
          </div>
        ) : (
          <div className={`similar-card__poster similar-card__poster--${theme}`}>
            <div className="similar-card__poster-glow" />
            <div className={`similar-card__poster-title ${titleSizeClass}`}>
              {title}
            </div>
          </div>
        )}
      </div>

      <div className="similar-card__body">
        <div className="similar-card__title" title={title}>
          {title}
        </div>

        {wanted ? (
          <div className="similar-card__price similar-card__price--wanted">
            Шукаю
          </div>
        ) : (
          Number.isFinite(priceNum) &&
          priceNum > 0 && (
            <div className="similar-card__price">
              {priceNum.toLocaleString("uk-UA")} {item.currency || "₴"}
            </div>
          )
        )}
      </div>
    </Link>
  );
};

const SimilarListings = ({ listingId, category }) => {
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tried, setTried] = useState(false);
  const sentinelRef = useRef(null);

  const currentContext = useMemo(
    () => ({ id: listingId, category: sanitize(category) }),
    [listingId, category]
  );

  const fetchAndRank = useCallback(async () => {
    if (!currentContext.id || loading || tried) return;
    setLoading(true);

    try {
      // Bounded (paginated=1) call instead of the old unfiltered fetch of
      // every listing on the marketplace (P0 #1, 2026-08-29 RAM/stability
      // fix). limit=150 keeps a wide enough candidate pool for the scoring
      // below at today's inventory size without being unbounded; the
      // existing scoring/dedup logic is untouched, it just runs over a
      // capped pool instead of the entire table.
      const { data } = await axios.get(`${API_BASE}/api/listings/feed/all`, {
        params: { paginated: 1, limit: 150 },
        validateStatus: (s) => s >= 200 && s < 300,
      });

      const items = Array.isArray(data?.items) ? data.items : [];

      if (!items.length) {
        setSimilar([]);
        return;
      }

      const currentItem =
        items.find((it) => (it.listingId || it.id) === currentContext.id) || {
          title: "",
          category: currentContext.category || "",
        };

      const candidates = items.filter((it) => {
        const id = it.listingId || it.id;
        if (!id || id === currentContext.id) return false;
        return (it.status || "active").toLowerCase() === "active";
      });

      const scored = candidates.map((it) => ({
        item: it,
        score: scoreSimilarity(
          {
            title: currentItem.title || "",
            category: currentItem.category || currentContext.category || "",
          },
          { title: it.title || "", category: it.category || "" }
        ),
      }));

      const filtered = scored.filter((s) => s.score > 0);

      filtered.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return Number(b.item.createdAt || 0) - Number(a.item.createdAt || 0);
      });

      const uniq = new Map();

      for (const s of filtered) {
        const lid = s.item.listingId || s.item.id;
        if (!lid) continue;
        if (!uniq.has(lid)) uniq.set(lid, s.item);
        if (uniq.size >= 12) break;
      }

      setSimilar(Array.from(uniq.values()));
    } catch {
      setSimilar([]);
    } finally {
      setLoading(false);
      setTried(true);
    }
  }, [currentContext.id, currentContext.category, loading, tried]);

  useEffect(() => {
    setSimilar([]);
    setLoading(false);
    setTried(false);
  }, [currentContext.id, currentContext.category]);

  useEffect(() => {
    fetchAndRank();
  }, [fetchAndRank]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) fetchAndRank();
        });
      },
      { root: null, rootMargin: "400px 0px", threshold: 0.01 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchAndRank]);

  if (!loading && tried && similar.length === 0) return null;

  return (
    <section className="similar-listings">
      <div className="similar__head">
        <h3 className="similar__title">Схожі оголошення</h3>
      </div>

      {loading && tried && !similar.length && (
        <div className="similar__row similar__row--skeletons">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sim-skel">
              <div className="sim-skel__media" />
              <div className="sim-skel__line" />
              <div className="sim-skel__line sim-skel__line--short" />
            </div>
          ))}
        </div>
      )}

      {!loading && similar.length > 0 && (
        <div className="similar__row">
          {similar.map((it) => (
            <SimilarCard key={it.listingId || it.id} item={it} />
          ))}
        </div>
      )}

      <div ref={sentinelRef} className="similar__sentinel" aria-hidden="true" />
    </section>
  );
};

export default SimilarListings;